import React, { useState, useEffect } from 'react';
import { AuthContext } from './../AuthContext';
import Sidebar from './../Sidebar';
import Footer from './../Footer';
import { site_url } from "./../config.js";
import axios from 'axios';
import { useParams, useNavigate } from "react-router-dom";
import Swal from 'sweetalert2';

function CustomerEdit() {
  const { user } = React.useContext(AuthContext);
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    id: '',
    shop_name: '',
    shop_name_tamil: '',
    username: '',
    whatsapp_number: '',
    phone: '',
    address: '',
    en_address: '',
    city: '',
    state: '',
    pincode: '',
    shop_cate: '',
    gst: '',
    password: '',
    shop_img: null
  });
  
  const [shopCategories, setShopCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [phoneError, setPhoneError] = useState(false);
  const [showKeyboard, setShowKeyboard] = useState(false);
  const [previewImage, setPreviewImage] = useState('');

  // Tamil keyboard layout
  const tamilKeyboard = [
    ['அ', 'ஆ', 'இ', 'ஈ', 'உ', 'ஊ', 'எ', 'ஏ', 'ஐ', 'ஒ', 'ஓ', 'ஔ'],
    ['க', 'ங', 'ச', 'ஞ', 'ட', 'ண', 'த', 'ந', 'ப', 'ம', 'ய', 'ர'],
    ['ல', 'வ', 'ழ', 'ள', 'ற', 'ன', 'ஷ', 'ஸ', 'ஹ', 'க்ஷ', 'ஜ', 'ஸ்ரீ'],
    [' ', 'Backspace', 'Enter']
  ];

  // Fetch customer data
  useEffect(() => {
    if (id) {
      fetchCustomerData();
    }
    fetchShopCategories();
  }, [id]);

  const fetchCustomerData = async () => {
    try {
      const response = await axios.get(site_url + `/vendor/details/${id}`);
      if (response.data.status === 'success') {
        const customer = response.data.vendor;
        setFormData({
          id: customer.id || '',
          shop_name: customer.shop_name || '',
          shop_name_tamil: customer.shop_name_tamil || '',
          username: customer.username || '',
          whatsapp_number: customer.whatsapp_number || '',
          phone: customer.phone || '',
          address: customer.address || '',
          en_address: customer.en_address || '',
          city: customer.city || '',
          state: customer.state || '',
          pincode: customer.pincode || '',
          shop_cate: customer.shop_cate || '',
          gst: customer.gst || '',
          password: '', // Always empty for security
          shop_img: null
        });
        setPreviewImage(customer.shop_img || '');
      }
    } catch (error) {
      console.error('Error fetching customer data:', error);
      Swal.fire('Error', 'Failed to fetch customer data', 'error');
    }
  };

  const fetchShopCategories = async () => {
    try {
      const response = await axios.get(site_url + '/vendor/shopcategory');
      if (response.data.status === 'success') {
        setShopCategories(response.data.category);
      }
    } catch (error) {
      console.error('Error fetching shop categories:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    
    if (name === 'shop_img' && files[0]) {
      const file = files[0];
      setFormData(prev => ({
        ...prev,
        [name]: file
      }));
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImage(e.target.result);
      };
      reader.readAsDataURL(file);
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handlePhoneCheck = async (phone) => {
    if (phone && phone !== formData.phone) {
      try {
        const response = await axios.get(site_url + `/vendor/customer_check/${phone}`);
        setPhoneError(response.data.exists);
      } catch (error) {
        console.error('Error checking phone:', error);
      }
    } else {
      setPhoneError(false);
    }
  };

  const handleTamilInput = (char) => {
    if (char === 'Backspace') {
      setFormData(prev => ({
        ...prev,
        shop_name_tamil: prev.shop_name_tamil.slice(0, -1)
      }));
    } else if (char === 'Enter') {
      setShowKeyboard(false);
    } else if (char === ' ') {
      setFormData(prev => ({
        ...prev,
        shop_name_tamil: prev.shop_name_tamil + ' '
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        shop_name_tamil: prev.shop_name_tamil + char
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (phoneError) {
      Swal.fire('Error', 'Phone number already exists', 'error');
      return;
    }

    setLoading(true);
    
    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== '') {
          formDataToSend.append(key, formData[key]);
        }
      });

      const response = await axios.post(site_url + '/vendor/update', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.status === 'success') {
        Swal.fire('Success', 'Customer updated successfully', 'success');
        navigate('/customer');
      } else {
        Swal.fire('Error', response.data.message || 'Failed to update customer', 'error');
      }
    } catch (error) {
      console.error('Error updating customer:', error);
      Swal.fire('Error', 'Failed to update customer', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div id="appCapsule">
        {/* Header */}
        <div className="section mt-2">
          <div className="section-heading">
            <h2>Edit Customer</h2>
          </div>
        </div>

        {/* Form */}
        <div className="section">
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group col-md-6">
                <label htmlFor="shop_name">Shop Name</label>
                <input 
                  type="hidden" 
                  value={formData.id} 
                  name="id" 
                  className="form-control"
                />
                <input 
                  type="text" 
                  value={formData.shop_name} 
                  name="shop_name" 
                  className="form-control" 
                  required 
                  placeholder="Shop Name"
                  onChange={handleInputChange}
                />
              </div>
              
              <div className="form-group col-md-6">
                <label htmlFor="shop_name_tamil">Shop Tamil Name</label>
                <input 
                  type="text" 
                  id="tamilInput"
                  value={formData.shop_name_tamil} 
                  name="shop_name_tamil" 
                  className="form-control" 
                  required 
                  placeholder="Shop Tamil Name"
                  onFocus={() => setShowKeyboard(true)}
                  onChange={handleInputChange}
                />
              </div>
              
              <div className="form-group col-md-12">
                <div 
                  id="keyboard" 
                  className="col-md-12" 
                  style={{ display: showKeyboard ? 'block' : 'none' }}
                >
                  <div className="tamil-keyboard">
                    {tamilKeyboard.map((row, rowIndex) => (
                      <div key={rowIndex} className="keyboard-row">
                        {row.map((char, charIndex) => (
                          <button
                            key={charIndex}
                            type="button"
                            className={`keyboard-key ${char === ' ' ? 'space-key' : ''}`}
                            onClick={() => handleTamilInput(char)}
                          >
                            {char}
                          </button>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="form-group col-md-6">
                <label htmlFor="username">Owner Name</label>
                <input 
                  type="text" 
                  value={formData.username} 
                  name="username" 
                  className="form-control" 
                  required 
                  placeholder="Owner Name"
                  onChange={handleInputChange}
                />
              </div>
           
              <div className="form-group col-md-6">
                <label htmlFor="shop_img">Shop Image</label>
                {previewImage && (
                  <div className="mb-2">
                    <img 
                      src={previewImage} 
                      alt="Shop Preview" 
                      style={{width: '100px', height: '100px', objectFit: 'cover'}}
                    />
                  </div>
                )}
                <input 
                  type="file" 
                  name="shop_img" 
                  className="form-control"
                  accept="image/*"
                  onChange={handleInputChange}
                />
              </div>
              
              <div className="form-group col-md-6">
                <label htmlFor="whatsapp_number">Whatsapp Number</label>
                <input 
                  type="number" 
                  value={formData.whatsapp_number} 
                  name="whatsapp_number" 
                  className="form-control" 
                  required 
                  placeholder="Whatsapp Number"
                  onChange={handleInputChange}
                />
              </div>
        
              <div className="form-group col-md-6">
                <label htmlFor="phone">
                  Phone Number <span className="text-danger">(*Phone number must be unique)</span>
                </label>
                <input 
                  value={formData.phone} 
                  type="number" 
                  name="phone" 
                  className="form-control" 
                  required 
                  placeholder="Phone Number"
                  onChange={(e) => {
                    handleInputChange(e);
                    handlePhoneCheck(e.target.value);
                  }}
                />
                
                {phoneError && (
                  <div className="alert alert-danger mt-3" role="alert">
                    Phone number already exists
                  </div>
                )}
              </div>
              
              <div className="form-group col-md-6" style={{ display: 'none' }}>
                <label htmlFor="password">Password</label>
                <input 
                  value={formData.password} 
                  type="password" 
                  name="password" 
                  className="form-control"
                  onChange={handleInputChange}
                />
              </div>

              {/* Additional Address Fields */}
              <div className="form-group col-md-12">
                <label htmlFor="address">Address (Tamil)</label>
                <input 
                  type="text" 
                  value={formData.address} 
                  name="address" 
                  className="form-control" 
                  placeholder="Address in Tamil"
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group col-md-12">
                <label htmlFor="en_address">Address (English)</label>
                <input 
                  type="text" 
                  value={formData.en_address} 
                  name="en_address" 
                  className="form-control" 
                  placeholder="Address in English"
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group col-md-4">
                <label htmlFor="city">City</label>
                <input 
                  type="text" 
                  value={formData.city} 
                  name="city" 
                  className="form-control" 
                  placeholder="City"
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group col-md-4">
                <label htmlFor="state">State</label>
                <input 
                  type="text" 
                  value={formData.state} 
                  name="state" 
                  className="form-control" 
                  placeholder="State"
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group col-md-4">
                <label htmlFor="pincode">Pincode</label>
                <input 
                  type="text" 
                  value={formData.pincode} 
                  name="pincode" 
                  className="form-control" 
                  placeholder="Pincode"
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group col-md-6">
                <label htmlFor="shop_cate">Shop Category</label>
                <select 
                  value={formData.shop_cate} 
                  name="shop_cate" 
                  className="form-control"
                  onChange={handleInputChange}
                >
                  <option value="">Select Category</option>
                  {shopCategories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group col-md-6">
                <label htmlFor="gst">GST Number</label>
                <input 
                  type="text" 
                  value={formData.gst} 
                  name="gst" 
                  className="form-control" 
                  placeholder="GST Number"
                  onChange={handleInputChange}
                />
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="form-group mt-4">
              <div className="row" style={{width:'100%'}}>
                
                <div className="col-6">
                  <button 
                    type="button" 
                    className="btn btn-secondary w-100"
                    onClick={() => navigate('/customer')}
                  >
                    Cancel
                  </button>
                </div>
                <div className="col-6">
                  <button 
                    type="submit" 
                    className="btn btn-primary "
                    disabled={loading || phoneError}
                  >
                    {loading ? 'Updating...' : 'Update Customer'}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      <Footer />
      <Sidebar />
    </>
  );
}

// Add CSS styles for Tamil keyboard
const keyboardStyles = `
  .tamil-keyboard {
    background: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    padding: 10px;
    margin-top: 10px;
  }
  
  .keyboard-row {
    display: flex;
    justify-content: center;
    margin-bottom: 5px;
  }
  
  .keyboard-key {
    background: #fff;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    padding: 8px 12px;
    margin: 2px;
    cursor: pointer;
    font-size: 14px;
    min-width: 40px;
    text-align: center;
    transition: all 0.2s ease;
  }
  
  .keyboard-key:hover {
    background: #e9ecef;
    border-color: #adb5bd;
  }
  
  .keyboard-key:active {
    background: #dee2e6;
    transform: scale(0.95);
  }
  
  .space-key {
    min-width: 100px;
  }
  
  .form-row {
    display: flex;
    flex-wrap: wrap;
    margin-right: -15px;
    margin-left: -15px;
  }
  
  .form-group {
    margin-bottom: 1rem;
  }
  
  .col-md-6 {
    flex: 0 0 50%;
    max-width: 50%;
    padding-right: 15px;
    padding-left: 15px;
  }
  
  .col-md-12 {
    flex: 0 0 100%;
    max-width: 100%;
    padding-right: 15px;
    padding-left: 15px;
  }
  
  @media (max-width: 768px) {
    .col-md-6 {
      flex: 0 0 100%;
      max-width: 100%;
    }
    
    .keyboard-key {
      padding: 6px 8px;
      font-size: 12px;
      min-width: 30px;
    }
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement("style");
  styleSheet.type = "text/css";
  styleSheet.innerText = keyboardStyles;
  document.head.appendChild(styleSheet);
}

export default CustomerEdit;
